import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Container, Box, Typography, Paper, Alert, CircularProgress,
  Snackbar, useTheme, alpha, Grid, Card, CardContent, Stack,
  Fab, Drawer, List, ListItem, ListItemText, ListItemButton,
  IconButton, Menu, MenuItem, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button, Avatar,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Collapse, ListItemIcon, Fade,
  LinearProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Add as AddDepartmentIcon,
  Settings as SettingsIcon,

} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Import components
import DepartmentManager from './employees-components/DepartmentManager';

// GlowWork styled components
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 12,
  background: 'white',
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.grey[50], 0.5),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: 'translateX(4px)',
    transition: 'all 0.2s ease',
    boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
  },
  cursor: 'pointer',
  transition: 'all 0.2s ease'
}));

export default function Employees() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canEditPage, canAccessPage, canViewEmployees, canEditEmployees, canDeleteEmployees } = usePermissions();

  // State management
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  // Dialog states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departmentManagerOpen, setDepartmentManagerOpen] = useState(false);
  
  // Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEmployee, setMenuEmployee] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });



  const showAlert = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Load data
  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_employee_decrypted', {
          company_id_param: companyId
        });

      if (error) throw error;
      
      // Load departments and managers separately for the decrypted employees
      const employeeIds = data?.map(emp => emp.id) || [];
      const departmentIds = [...new Set(data?.map(emp => emp.department_id).filter(Boolean))] || [];
      const managerIds = [...new Set(data?.map(emp => emp.manager_id).filter(Boolean))] || [];
      
      // Load departments
      let departmentsData = [];
      if (departmentIds.length > 0) {
        const { data: deptData, error: deptError } = await supabase
          .from('departments')
          .select('id, name, hierarchy_level, color')
          .in('id', departmentIds);
        
        if (!deptError) departmentsData = deptData || [];
      }
      
      // Load managers
      let managersData = [];
      if (managerIds.length > 0) {
        const { data: mgrsData, error: mgrsError } = await supabase
          .from('employees')
          .select('id, first_name, last_name')
          .in('id', managerIds);
        
        if (!mgrsError) managersData = mgrsData || [];
      }
      
      // Combine the data
      const enrichedEmployees = (data || []).map(emp => ({
        ...emp,
        departments: departmentsData.find(dept => dept.id === emp.department_id),
        manager: managersData.find(mgr => mgr.id === emp.manager_id)
      }));
      
      setEmployees(enrichedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      showAlert('Error loading employees: ' + error.message, 'error');
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      
      setDepartments(data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
      showAlert('Error loading departments: ' + error.message, 'error');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadEmployees(), loadDepartments()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  useEffect(() => {
    if (window.location.hash === '#department-manager') {
      setDepartmentManagerOpen(true);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);


  // Filter employees based on selected department and search
  useEffect(() => {
    let filtered = employees;
    
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department_id === selectedDepartment.id);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredEmployees(filtered);
    setPage(0);
  }, [employees, selectedDepartment, searchTerm]);

  // Simple Department Item Component
  function DepartmentItem({ dept }) {
    const employeeCount = employees.filter(emp => emp.department_id === dept.id).length;

    return (
      <ListItemButton
        onClick={() => setSelectedDepartment(selectedDepartment?.id === dept.id ? null : dept)}
        selected={selectedDepartment?.id === dept.id}
        sx={{
          pl: 2,
          borderRadius: 0,
          mb: 0,
          backgroundColor: selectedDepartment?.id === dept.id ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            transform: 'translateX(4px)',
            transition: 'all 0.2s ease',
            boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
          },
          transition: 'all 0.2s ease'
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <BusinessIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
        </ListItemIcon>
        <ListItemText 
          primary={dept.name}
          secondary={`${employeeCount} employees`}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItemButton>
    );
  }


  // Employee actions
  const handleEmployeeMenuOpen = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setMenuEmployee(employee);
  };

  const handleEmployeeMenuClose = () => {
    setAnchorEl(null);
    setMenuEmployee(null);
  };

  const handleEditEmployee = (employee) => {
    navigate(`/company/${companyId}/employees/${employee.id}`);
    handleEmployeeMenuClose();
  };

  const handleDeleteEmployee = async (employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
    handleEmployeeMenuClose();
  };

  const confirmDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    
    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeToDelete.id);

      if (error) throw error;
      
      showAlert('Employee deleted successfully');
      await loadEmployees();
    } catch (error) {
      showAlert('Error deleting employee: ' + error.message, 'error');
    }
    
    setDeleteDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const handleAddEmployee = () => {
    navigate(`/company/${companyId}/employees/new`);
  };

  // Permission check
  if (!canViewEmployees()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Employee Management Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Employee Management. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#FFC107', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#FFC107', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ 
                color: '#FFC107',
                mb: 3,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <LinearProgress 
              sx={{ 
                mb: 3,
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha('#FFC107', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: '#FFC107'
                }
              }} 
            />
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#FFC107',
              mb: 1
            }}>
              Loading Employees...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Preparing your employee data
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl" 
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {/* Page Header */}
        <Card sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            opacity: 0.8
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}>
                <PeopleIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 500, 
                  color: 'primary.main', 
                  mb: 0.5,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Employee Management
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  Manage your team, departments, and organizational structure
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box display="flex" gap={3}>
                  {/* Left Sidebar - Departments */}
        <Paper sx={{ 
          width: 300, 
          minHeight: 'calc(100vh - 200px)', 
          p: 2,
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Departments
              </Typography>
              <Box>

              {canEditEmployees() && (
                <IconButton
                  size="small"
                  onClick={() => setDepartmentManagerOpen(true)}
                  title="Manage Departments"
                >
                  <SettingsIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          
          <List dense>
            <ListItemButton
              onClick={() => setSelectedDepartment(null)}
              selected={!selectedDepartment}
              sx={{ 
                borderRadius: 0, 
                mb: 0,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s ease',
                  boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PeopleIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText 
                primary="All Employees"
                secondary={`${employees.length} total`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Add Department Button */}
            {canEditEmployees() && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setDepartmentManagerOpen(true)}
                sx={{ 
                  mb: 2, 
                  fontWeight: 500,
                  borderStyle: 'dashed',
                  '&:hover': {
                    borderStyle: 'solid'
                  }
                }}
              >
                Manage Departments
              </Button>
            )}
            
            {departments.map(dept => (
              <DepartmentItem key={dept.id} dept={dept} />
            ))}
          </List>
        </Paper>

        {/* Main Content - Employee List */}
        <Box sx={{ flexGrow: 1 }}>
          <SectionPaper sx={{
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
                  {selectedDepartment ? selectedDepartment.name : 'All Employees'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredEmployees.length} employees
                </Typography>
              </Box>
              
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ minWidth: 250 }}
                />
                {(canEditEmployees() && (
                  canAccessPage('work_information') || 
                  canAccessPage('resume') || 
                  canAccessPage('certifications') || 
                  canAccessPage('personal_details')
                )) && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddEmployee}
                    sx={{ fontWeight: 500 }}
                  >
                    Add Employee
                  </Button>
                )}
              </Box>
            </Box>

            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer sx={{ 
                '&::-webkit-scrollbar': { display: 'none' },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
                overflow: 'auto'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Employee
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Contact
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Department
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Position
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Manager
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {paginatedEmployees.map((employee) => (
                    <StyledTableRow 
                      key={employee.id} 
                      hover
                      onClick={() => navigate(`/company/${companyId}/employees/${employee.id}`)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            src={employee.profile_image_url}
                            sx={{ 
                              width: 40, 
                              height: 40,
                              bgcolor: employee.departments?.color || theme.palette.grey[400]
                            }}
                          >
                            {employee.first_name?.[0]}{employee.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.first_name} {employee.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.employee_number}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {employee.email && (
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption">{employee.email}</Typography>
                            </Box>
                          )}
                          {employee.work_phone && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption">{employee.work_phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {employee.departments && (
                          <Chip 
                            label={employee.departments.name}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{employee.job_title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.job_position}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {employee.manager_name ? (
                          <Typography variant="body2">
                            {employee.manager_name}
                          </Typography>
                        ) : employee.manager ? (
                          <Typography variant="body2">
                            {employee.manager.first_name} {employee.manager.last_name}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        {(canEditEmployees() || canDeleteEmployees()) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEmployeeMenuOpen(e, employee);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ 
              borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              borderRadius: '0 0 12px 12px'
            }}>
              <TablePagination
                component="div"
                count={filteredEmployees.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                SelectProps={{
                  disableScrollLock: true,
                  disablePortal: false,
                  disableAutoFocus: true,
                  disableEnforceFocus: true,
                  MenuProps: {
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }
                }}
                sx={{ 
                  px: 3,
                  py: 2,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: theme.palette.text.primary
                  }
                }}
              />
            </Box>
            </Paper>
          </SectionPaper>
        </Box>
      </Box>

      {/* Employee Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleEmployeeMenuClose}
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuProps={{
          disableScrollLock: true,
          disablePortal: false,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          PaperProps: {
            sx: {
              position: 'absolute !important',
              contain: 'layout style paint',
              zIndex: 9999,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          },
          sx: {
            zIndex: 9999
          }
        }}
      >
        {canEditEmployees() && (
          <MenuItem onClick={() => handleEditEmployee(menuEmployee)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Employee</ListItemText>
          </MenuItem>
        )}
        {canDeleteEmployees() && (
          <MenuItem onClick={() => handleDeleteEmployee(menuEmployee)}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete Employee</ListItemText>
          </MenuItem>
        )}
      </Menu>



      {/* Department Manager Dialog */}
      {departmentManagerOpen && canEditEmployees() && (
        <DepartmentManager
          open={departmentManagerOpen}
          onClose={() => setDepartmentManagerOpen(false)}
          companyId={companyId}
          onDepartmentsChange={loadDepartments}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Employee
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{employeeToDelete?.first_name} {employeeToDelete?.last_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteEmployee}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Fade>
  );
}